import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  TextField,
  Button,
  Paper,
  Tooltip,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toast } from 'react-toastify'
import {
  CREATE_COMMENT,
  UPDATE_COMMENT,
  DELETE_COMMENT,
  GET_VOUCHER_BY_ID,
  GET_VOUCHER_COMMENTS,
} from './gql'
import { VoucherType } from '../../types'
import { useAuthentication } from '../../hooks/useAuthentication'
import VhsDialog from '../Common/VhsDialog'

type VoucherCommentsProps = {
  voucher: VoucherType
  hideTitle?: boolean
}

const VoucherComments: React.FC<VoucherCommentsProps> = ({
  voucher,
  hideTitle = false,
}) => {
  const { authState } = useAuthentication()
  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery(GET_VOUCHER_COMMENTS, {
    variables: { id: voucher.id },
    context: { headers: { authorization: authState.authToken } },
    skip: !voucher.id,
  })

  const [createCommentMutation] = useMutation(CREATE_COMMENT)
  const [updateCommentMutation] = useMutation(UPDATE_COMMENT)
  const [deleteCommentMutation] = useMutation(DELETE_COMMENT)

  const currentComments = data?.voucher?.comments || voucher.comments || []

  const [newCommentText, setNewCommentText] = useState('')
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editCommentText, setEditCommentText] = useState('')
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null)

  const handleAddComment = async () => {
    if (!newCommentText.trim()) return
    try {
      await createCommentMutation({
        variables: {
          input: {
            voucher_id: voucher.id,
            comment: newCommentText,
          },
        },
        awaitRefetchQueries: true,
      })
      await refetch()
      setNewCommentText('')
      toast.success('Comment added successfully')
    } catch (err: any) {
      toast.error(err.message || 'Failed to add comment')
    }
  }

  const handleUpdateComment = async (id: string) => {
    if (!editCommentText.trim()) return
    try {
      await updateCommentMutation({
        variables: {
          id,
          input: {
            voucher_id: voucher.id,
            comment: editCommentText,
          },
        },
        awaitRefetchQueries: true,
      })
      await refetch()
      setEditingCommentId(null)
      setEditCommentText('')
      toast.success('Comment updated successfully')
    } catch (err: any) {
      toast.error(err.message || 'Failed to update comment')
    }
  }

  const handleDeleteComment = async (id: string) => {
    setDeleteCommentId(id)
  }

  const handleConfirmDeleteComment = async () => {
    if (!deleteCommentId) return
    try {
      await deleteCommentMutation({
        variables: { id: deleteCommentId },
        awaitRefetchQueries: true,
      })
      await refetch()
      setDeleteCommentId(null)
      toast.success('Comment deleted successfully')
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete comment')
    }
  }

  if (!voucher.id) return null

  return (
    <Box mt={hideTitle ? 0 : 6} mb={hideTitle ? 0 : 6}>
      {!hideTitle && (
        <Typography
          variant="h6"
          sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}
        >
          Comments
        </Typography>
      )}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            placeholder="Write a comment..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            sx={{ backgroundColor: 'background.paper' }}
          />
          <Box display="flex" justifyContent="flex-end" gap={1}>
            {newCommentText.trim() && (
              <Button variant="text" onClick={() => setNewCommentText('')}>
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              disableElevation
              endIcon={<SendIcon />}
              onClick={handleAddComment}
              disabled={!newCommentText.trim()}
            >
              Post Comment
            </Button>
          </Box>
        </Box>
      </Paper>
      <Box mb={3}>
        {currentComments.map((c: any) => (
          <Card
            key={c.id}
            sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {c.user?.first_name || 'Unknown'}{' '}
                  {c.user?.last_name || 'User'} -{' '}
                  {new Date(
                    isNaN(Number(c.created_at))
                      ? c.created_at
                      : Number(c.created_at),
                  ).toLocaleString()}
                </Typography>
                {c.user?.id === authState.authUser.id && (
                  <Box display="flex" gap={1}>
                    <Tooltip title="Edit Comment">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingCommentId(c.id)
                          setEditCommentText(c.comment)
                        }}
                        sx={{
                          backgroundColor: 'info.light',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'info.main',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Comment">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteComment(c.id)}
                        sx={{
                          backgroundColor: 'error.light',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'error.main',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              {editingCommentId === c.id ? (
                <Box display="flex" gap={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    value={editCommentText}
                    onChange={(e) => setEditCommentText(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => handleUpdateComment(c.id)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setEditingCommentId(null)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {c.comment}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Delete Confirmation Dialog */}
      <VhsDialog
        open={deleteCommentId !== null}
        onClose={() => setDeleteCommentId(null)}
        onConfirm={handleConfirmDeleteComment}
        title="Delete Comment"
        warningMessage="Are you sure you want to delete this comment?"
        confirmButtonText="Delete"
        color="error"
        showReasonField={false}
      />
    </Box>
  )
}

export default VoucherComments
